import { type BaseQueryApi, createApi, type FetchArgs } from "@reduxjs/toolkit/query/react";

import { fetchWithAuth } from "~/common/fetch";
import type { TPatient } from "~/types/patient";

export type TYooKassaPaymentStatus = "pending" | "succeeded" | "canceled";

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: ["Event", "MeetingFeedback", "Toast"],
  endpoints: (builder) => ({
    getYear: builder.query<{ year: number }, void>({
      query: () => ({ url: "year" }),
    }),
    getInvoicePaymentStatus: builder.query<
      { status: TYooKassaPaymentStatus },
      { patientId: TPatient["patient_id"]; invoiceId: number; patientToken?: string }
    >({
      query: (params) => {
        const args: { token?: string; patient_id?: number } = {};

        if (typeof params.patientToken == "string") {
          args.token = params.patientToken;
          args.patient_id = params.patientId;
        }

        return {
          url: `doctor/patients/${params.patientId}/invoice/${params.invoiceId}`,
          params: args,
        };
      },
    }),
    getInvoicePaymentBatchStatus: builder.query<
      { status: TYooKassaPaymentStatus },
      { batchId: string }
    >({
      query: (params) => ({ url: `doctor/payments/invoices/batch/${params.batchId}` }),
    }),
    getAdditionalAlignersPatients: builder.query<
      { id: TPatient["patient_id"]; name: string; steps: number }[],
      void
    >({
      query: () => ({ url: "doctor/patients/additional-aligners" }),
    }),
  }),
});

async function fetchWithAuthBaseQuery(args: string | FetchArgs, api: BaseQueryApi) {
  try {
    const fetchArgs = typeof args == "string" ? { url: args } : args;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { url, method = "GET", body, headers, params } = fetchArgs;
    const searchParams = new URLSearchParams(params);

    const response = await fetchWithAuth(
      `${process.env.API}/${url}` + (searchParams.size > 0 ? `?${searchParams.toString()}` : ""),
      {
        method,
        body: body ? JSON.stringify(body) : undefined,
        headers: headers as HeadersInit,
        signal: api.signal,
      },
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data,
        },
      };
    }

    return { data };
  } catch (err) {
    return {
      error: {
        status: "FETCH_ERROR",
        data: (err as Error).message,
      },
    };
  }
}

export const {
  useGetInvoicePaymentStatusQuery,
  useGetInvoicePaymentBatchStatusQuery,
  useGetAdditionalAlignersPatientsQuery,
} = api;
